import { useTranslation } from "react-i18next";
import { faEllipsis, faRotateRight } from "@fortawesome/pro-regular-svg-icons";

import { Icon } from "@/components";

import { webhookTriggerLabelKey } from "../components/WebhookListsCell";
import { deliveryAttempts, deliveryStatusDetail, deliveryTime } from "./deliveryPresentation";

import type { WebhookDelivery } from "../schema/webhookDelivery";

export function DeliveryStatus({ row }: { row: WebhookDelivery }) {
	const { t } = useTranslation("translation", { keyPrefix: "webhookHealth.deliveries" });
	const tone =
		row.status === "DELIVERED"
			? "bg-status-success-light text-status-success"
			: row.status === "GIVEN_UP"
				? "bg-status-error-light text-status-error"
				: "bg-status-warning-light text-text";

	return (
		<div className="flex flex-col items-start gap-1">
			<span className={`rounded-full px-2 py-0.5 text-xs ${tone}`}>{t(row.status)}</span>
			<span className="text-xs text-text-meta">{deliveryStatusDetail(row, t)}</span>
			{row.isRedelivery && (
				<span className="rounded-full bg-brand-light/10 px-2 text-xs text-brand-light">
					{t("redelivery")}
				</span>
			)}
		</div>
	);
}
export function DeliveryTrigger({ trigger }: { trigger: string | null }) {
	const { t } = useTranslation();
	const { t: dt } = useTranslation("translation", { keyPrefix: "webhookHealth.deliveries" });

	return (
		<span className="rounded-full bg-status-info-light px-2 py-0.5 text-xs text-text-meta">
			{trigger === "MANUAL_SEND"
				? dt("manual")
				: trigger
					? t(webhookTriggerLabelKey(trigger), { lng: "en" })
					: dt("unknown")}
		</span>
	);
}
export function DeliveryTable({
	rows,
	onDetail,
	onRedeliver,
}: {
	rows: WebhookDelivery[];
	onDetail: (id: number) => void;
	onRedeliver: (row: WebhookDelivery) => void;
}) {
	const { t } = useTranslation("translation", { keyPrefix: "webhookHealth.deliveries" });

	return (
		<div className="overflow-x-auto">
			<table className="w-full text-left text-sm text-text">
				<thead className="bg-background-light text-xs uppercase text-text-meta">
					<tr>
						{["time", "lead", "trigger", "status", "attempts", "http", "excerpt"].map((key) => (
							<th key={key} className="px-3 py-3">
								{t(key)}
							</th>
						))}
						<th className="px-3 py-3">
							<span className="sr-only">{t("details")}</span>
						</th>
					</tr>
				</thead>
				<tbody>
					{rows.map((row) => (
						<tr
							key={row.id}
							className={`border-b border-border-light ${row.status === "GIVEN_UP" ? "bg-status-error-light/20" : ""}`}
						>
							<td className="px-3 py-3 align-top whitespace-nowrap">
								{deliveryTime(row.createdAt)}
							</td>
							<td className="px-3 py-3 align-top">
								{row.lead?.name ?? t("removed")}
								<div className="text-xs text-text-meta">{row.lead?.company}</div>
							</td>
							<td className="px-3 py-3 align-top">
								<DeliveryTrigger trigger={row.trigger} />
							</td>
							<td className="px-3 py-3 align-top">
								<DeliveryStatus row={row} />
							</td>
							<td className="px-3 py-3 align-top whitespace-nowrap">{deliveryAttempts(row, t)}</td>
							<td className="px-3 py-3 align-top font-mono">
								{row.givenUpKind === "NOT_SENT" ? "–" : (row.lastHttpStatus ?? "–")}
							</td>
							<td className="max-w-64 px-3 py-3 align-top">
								<div
									className="truncate font-mono text-xs text-text-meta"
									title={row.lastResponseExcerpt ?? row.lastError ?? ""}
								>
									{row.lastResponseExcerpt ?? row.lastError ?? "–"}
								</div>
							</td>
							<td className="px-3 py-3 align-top">
								<div className="flex gap-3">
									{row.status === "GIVEN_UP" && (
										<button
											aria-label={t("redeliver")}
											className="cursor-pointer text-text-meta"
											type="button"
											onClick={() => onRedeliver(row)}
										>
											<Icon icon={faRotateRight} />
										</button>
									)}
									<button
										aria-label={t("details")}
										className="cursor-pointer text-text-meta"
										type="button"
										onClick={() => onDetail(row.id)}
									>
										<Icon icon={faEllipsis} />
									</button>
								</div>
							</td>
						</tr>
					))}
				</tbody>
			</table>
		</div>
	);
}
